/*
  Vee custom Shopify Subscriptions widget

  Purpose:
  Converts the native Shopify Subscriptions widget into a cleaner layout:
  - Hides the visible "One-time purchase" radio row
  - Shows only a custom Schedule and Save checkbox
  - Pulls checkbox/helper wording from product metafields when available
  - Falls back to default wording when metafields are empty
  - Keeps the product as one-time purchase when the checkbox is unticked
  - Shows delivery schedule options only when the checkbox is ticked
  - Switches back to one-time purchase when the checkbox is unticked
  - Hides Shopify's default "$25.00 AUD" price text inside the widget
  - Places the helper subtext inside the same Schedule and Save box

  Frequency lock:
  - If the cart already has a Schedule and Save item, this script locks
    future Schedule and Save choices to the same delivery frequency.
  - Example: if the cart already has "Deliver every 4 months",
    the next product page will only allow "Deliver every 4 months".
  - The lock updates in real time after AJAX add-to-cart/cart updates.

  Metafields used:
  - custom.subscription_checkbox_text
  - custom.subscription_helper_text

  Important:
  This only changes the product-page display and interaction.
  It does not create or apply the real discount at checkout.
*/

(function () {
  console.log('Vee subscription script loaded');

  let cartFrequencyLock = null;
  let isRefreshingCartFrequencyLock = false;

  function getSubscriptionCopy() {
    const copyElement = document.querySelector('#vee-subscription-copy');

    return {
      checkboxText:
        copyElement?.dataset.checkboxText ||
        'Schedule and Save',

      helperText:
        copyElement?.dataset.helperText ||
        'Fresh pairs, delivered when you need them. <a href="/pages/schedule-and-save" target="_blank" rel="noopener noreferrer">Skip or cancel anytime.</a>'
    };
  }

  function triggerNativeChange(input) {
    if (!input) return;

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function normalizeText(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function getFrequencyFromText(text) {
    const normalText = normalizeText(text);

    const match = normalText.match(
      /(?:deliver|delivery)?\s*every\s+(\d+)\s+(week|weeks|month|months)/
    );

    if (!match) return null;

    const number = match[1];
    const unit = match[2].startsWith('week') ? 'week' : 'month';
    const labelUnit = number === '1' ? unit : unit + 's';

    return {
      key: number + '-' + unit,
      label: 'deliver every ' + number + ' ' + labelUnit
    };
  }

  function getFrequencyFromSellingPlanInput(input) {
    if (!input) return null;

    const row =
      input.closest('.group_list_item') ||
      input.closest('label') ||
      input.parentElement;

    if (!row) return null;

    return getFrequencyFromText(row.textContent);
  }

  function getFrequencyFromCartItem(item) {
    if (!item || !item.selling_plan_allocation) return null;

    const sellingPlan = item.selling_plan_allocation.selling_plan || {};
    const possibleText = [];

    if (sellingPlan.name) {
      possibleText.push(sellingPlan.name);
    }

    if (Array.isArray(sellingPlan.options)) {
      sellingPlan.options.forEach(function (option) {
        if (option.name) possibleText.push(option.name);
        if (option.value) possibleText.push(option.value);
      });
    }

    if (item.properties) {
      Object.keys(item.properties).forEach(function (key) {
        possibleText.push(key);
        possibleText.push(item.properties[key]);
      });
    }

    possibleText.push(item.title);
    possibleText.push(item.product_title);
    possibleText.push(item.variant_title);

    return getFrequencyFromText(possibleText.join(' '));
  }

  function shouldCartItemLockFrequency(item) {
    if (!item || !item.selling_plan_allocation) return false;

    /*
      Current rule:
      Any existing Schedule and Save subscription item in the cart locks
      the delivery frequency for the next Schedule and Save product.

      If you later only want this for underwear, we can narrow this by
      product title, handle, type, tag, or product ID.
    */
    return true;
  }

  function loadCartFrequencyLock() {
    cartFrequencyLock = null;

    return fetch('/cart.js', {
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Could not load cart');
        }

        return response.json();
      })
      .then(function (cart) {
        const items = cart.items || [];

        for (let i = 0; i < items.length; i += 1) {
          const item = items[i];

          if (!shouldCartItemLockFrequency(item)) continue;

          const frequency = getFrequencyFromCartItem(item);

          if (frequency) {
            cartFrequencyLock = frequency;
            break;
          }
        }
      })
      .catch(function () {
        cartFrequencyLock = null;
      });
  }

  function getInputsByName(name, radioType) {
    return Array.from(
      document.querySelectorAll('input[data-radio-type="' + radioType + '"]')
    ).filter(function (input) {
      return input.getAttribute('name') === name;
    });
  }

  function hidePriceText(container) {
    const prices = container.querySelectorAll('.in_widget_price');

    prices.forEach(function (price) {
      price.textContent = '';
      price.style.display = 'none';
    });
  }

  function hideScheduleAndSaveTitle(scheduleGroup) {
    const groupName = scheduleGroup.querySelector('.group_name');

    if (groupName) {
      groupName.style.display = 'none';
    }
  }

  function removeSavingTextFromSellingPlans(sellingInputs) {
    sellingInputs.forEach(function (input) {
      const row = input.closest('.group_list_item');
      if (!row) return;

      const existingSavingText = row.querySelector('.vee-saving-inline');
      if (existingSavingText) {
        existingSavingText.remove();
      }
    });
  }

  function styleSubscribeToggle(toggle) {
    toggle.style.display = 'flex';
    toggle.style.alignItems = 'center';
    toggle.style.gap = '12px';
    toggle.style.cursor = 'pointer';
    toggle.style.margin = '0';
    toggle.style.letterSpacing = '0.18em';
    toggle.style.textTransform = 'uppercase';
    toggle.style.fontWeight = '600';
  }

  function styleHelperText(helper) {
    helper.style.margin = '10px 0 0 34px';
    helper.style.fontSize = '14px';
    helper.style.lineHeight = '1.5';
    helper.style.letterSpacing = '0';
    helper.style.textTransform = 'none';
    helper.style.fontWeight = '400';
  }

  function styleScheduleList(groupList) {
    groupList.style.margin = '12px 0 0 34px';
    groupList.style.padding = '0';
    groupList.style.listStyle = 'none';
  }

  function hideOneTimePurchaseRow(oneTimeInput) {
    const oneTimeLabel = oneTimeInput.closest('label');

    if (oneTimeLabel) {
      oneTimeLabel.style.display = 'none';
    }
  }

  function hideNativeHelperText(outerContainer) {
    const parent = outerContainer.parentElement || document;

    const helperTexts = Array.from(parent.querySelectorAll('*')).filter(function (el) {
      const text = el.textContent.trim();

      // Do NOT hide our custom helper text inside the Schedule and Save box.
      if (el.classList.contains('vee-subscribe-helper-inside')) {
        return false;
      }

      return (
        el.children.length === 0 &&
        (
          text === 'Auto-renews, skip or cancel anytime.' ||
          el.classList.contains('vee-subscribe-helper')
        )
      );
    });

    helperTexts.forEach(function (helper) {
      helper.style.display = 'none';
    });
  }

  function getLockedSellingInput(sellingInputs) {
    if (!cartFrequencyLock) return null;

    return sellingInputs.find(function (input) {
      const frequency = getFrequencyFromSellingPlanInput(input);

      return frequency && frequency.key === cartFrequencyLock.key;
    }) || null;
  }

  function applyFrequencyLock(sellingInputs) {
    if (!cartFrequencyLock) {
      sellingInputs.forEach(function (input) {
        const row =
          input.closest('.group_list_item') ||
          input.closest('label') ||
          input.parentElement;

        input.disabled = false;

        if (row) {
          row.style.display = '';
        }
      });

      return;
    }

    const lockedInput = getLockedSellingInput(sellingInputs);

    if (!lockedInput) return;

    sellingInputs.forEach(function (input) {
      const row =
        input.closest('.group_list_item') ||
        input.closest('label') ||
        input.parentElement;

      const frequency = getFrequencyFromSellingPlanInput(input);
      const isMatchingFrequency =
        frequency && frequency.key === cartFrequencyLock.key;

      input.disabled = !isMatchingFrequency;

      if (row) {
        row.style.display = isMatchingFrequency ? '' : 'none';
      }
    });
  }

  function updateFrequencyLockMessage(scheduleGroup, groupList) {
  const message = scheduleGroup.querySelector('.vee-frequency-lock-message');

  if (message) {
    message.remove();
  }

  return null;
}

  function enhanceOneWidget(oneTimeInput) {
    const copy = getSubscriptionCopy();

    const optionName = oneTimeInput.getAttribute('name');
    const sellingInputs = getInputsByName(optionName, 'selling_plan');

    if (!sellingInputs.length) return false;

    const firstSellingInput = sellingInputs[0];
    const groupList = firstSellingInput.closest('.group_list');

    if (!groupList) return false;

    const scheduleGroup = groupList.parentElement;

    if (!scheduleGroup) return false;

    const outerContainer = scheduleGroup.parentElement || scheduleGroup;

    hidePriceText(outerContainer);
    hideScheduleAndSaveTitle(scheduleGroup);
    removeSavingTextFromSellingPlans(sellingInputs);
    hideOneTimePurchaseRow(oneTimeInput);
    styleScheduleList(groupList);
    hideNativeHelperText(outerContainer);
    applyFrequencyLock(sellingInputs);

    const lockedSellingInput = getLockedSellingInput(sellingInputs);
    const defaultSellingInput = lockedSellingInput || firstSellingInput;

    // Prevent duplicate setup.
    if (scheduleGroup.dataset.veeEnhanced === 'true') {
      const existingText = scheduleGroup.querySelector('.vee-subscribe-toggle span');
      const existingHelper = scheduleGroup.querySelector('.vee-subscribe-helper-inside');
      const existingLockMessage = updateFrequencyLockMessage(scheduleGroup, groupList);

      if (existingText) {
        existingText.textContent = copy.checkboxText;
      }

      if (existingHelper) {
        existingHelper.innerHTML = copy.helperText;
      }

      if (existingLockMessage) {
        existingLockMessage.style.display =
          groupList.style.display === 'none' ? 'none' : 'block';
      }

      hideNativeHelperText(outerContainer);

      return true;
    }

    scheduleGroup.dataset.veeEnhanced = 'true';

    // Create custom Schedule and Save checkbox row.
    const subscribeToggle = document.createElement('label');
    subscribeToggle.className = 'vee-subscribe-toggle';

    const subscribeCheckbox = document.createElement('input');
    subscribeCheckbox.type = 'checkbox';
    subscribeCheckbox.className = 'vee-subscribe-checkbox';
    subscribeCheckbox.style.width = '22px';
    subscribeCheckbox.style.height = '22px';
    subscribeCheckbox.style.margin = '0';

    const subscribeText = document.createElement('span');
    subscribeText.textContent = copy.checkboxText;

    subscribeToggle.appendChild(subscribeCheckbox);
    subscribeToggle.appendChild(subscribeText);

    styleSubscribeToggle(subscribeToggle);

    // Create helper text inside the same Schedule and Save box.
    const helperLine = document.createElement('div');
    helperLine.className = 'vee-subscribe-helper-inside';
    helperLine.innerHTML = copy.helperText;
    styleHelperText(helperLine);

    // Insert checkbox and helper above delivery schedule list.
    scheduleGroup.insertBefore(subscribeToggle, groupList);
    scheduleGroup.insertBefore(helperLine, groupList);

    const frequencyLockMessage = updateFrequencyLockMessage(scheduleGroup, groupList);

    function setFrequencyLockMessageVisibility(isVisible) {
      const message = scheduleGroup.querySelector('.vee-frequency-lock-message');

      if (message) {
        message.style.display = isVisible ? 'block' : 'none';
      }
    }

    function openSubscriptionOptions() {
      applyFrequencyLock(sellingInputs);

      groupList.style.display = 'block';
      setFrequencyLockMessageVisibility(true);

      const selectedSellingPlan = sellingInputs.find(function (input) {
        return input.checked && !input.disabled;
      });

      if (!selectedSellingPlan) {
        defaultSellingInput.checked = true;
        triggerNativeChange(defaultSellingInput);
      }
    }

    function closeSubscriptionOptions() {
      groupList.style.display = 'none';
      setFrequencyLockMessageVisibility(false);

      oneTimeInput.checked = true;
      triggerNativeChange(oneTimeInput);
    }

    subscribeCheckbox.addEventListener('change', function () {
      if (subscribeCheckbox.checked) {
        openSubscriptionOptions();
      } else {
        closeSubscriptionOptions();
      }
    });

    sellingInputs.forEach(function (sellingInput) {
      sellingInput.addEventListener('change', function () {
        if (sellingInput.checked) {
          subscribeCheckbox.checked = true;
          applyFrequencyLock(sellingInputs);
          groupList.style.display = 'block';
          setFrequencyLockMessageVisibility(true);
        }
      });
    });

    // Initial state.
    const hasSelectedSellingPlan = sellingInputs.some(function (input) {
      return input.checked && !input.disabled;
    });

    if (hasSelectedSellingPlan) {
      subscribeCheckbox.checked = true;
      groupList.style.display = 'block';
      setFrequencyLockMessageVisibility(true);
    } else {
      subscribeCheckbox.checked = false;
      closeSubscriptionOptions();
    }

    if (frequencyLockMessage) {
      frequencyLockMessage.style.display =
        subscribeCheckbox.checked ? 'block' : 'none';
    }

    return true;
  }

  function enhanceSubscriptionWidgets() {
    const oneTimeInputs = document.querySelectorAll(
      'input[data-radio-type="one_time_purchase"]'
    );

    oneTimeInputs.forEach(function (oneTimeInput) {
      enhanceOneWidget(oneTimeInput);
    });
  }

  function runRepeatedly() {
    enhanceSubscriptionWidgets();

    setTimeout(enhanceSubscriptionWidgets, 300);
    setTimeout(enhanceSubscriptionWidgets, 800);
    setTimeout(enhanceSubscriptionWidgets, 1500);
    setTimeout(enhanceSubscriptionWidgets, 3000);
  }

  function refreshCartFrequencyLock() {
    if (isRefreshingCartFrequencyLock) return;

    isRefreshingCartFrequencyLock = true;

    loadCartFrequencyLock()
      .then(function () {
        enhanceSubscriptionWidgets();

        setTimeout(enhanceSubscriptionWidgets, 300);
        setTimeout(enhanceSubscriptionWidgets, 800);
        setTimeout(enhanceSubscriptionWidgets, 1500);
      })
      .finally(function () {
        isRefreshingCartFrequencyLock = false;
      });
  }

  function getFetchUrl(fetchArguments) {
    const firstArgument = fetchArguments[0];

    if (typeof firstArgument === 'string') {
      return firstArgument;
    }

    if (firstArgument && typeof firstArgument.url === 'string') {
      return firstArgument.url;
    }

    return String(firstArgument || '');
  }

  function isCartRequest(url) {
    const urlText = String(url || '');

    return (
      urlText.includes('/cart/add') ||
      urlText.includes('/cart/change') ||
      urlText.includes('/cart/update') ||
      urlText.includes('/cart/clear')
    );
  }

  function watchCartChanges() {
    if (window.__veeCartWatcherActive) return;

    window.__veeCartWatcherActive = true;

    const originalFetch = window.fetch;

    window.fetch = function () {
      const fetchArguments = arguments;
      const requestUrl = getFetchUrl(fetchArguments);

      return originalFetch.apply(this, fetchArguments).then(function (response) {
        if (isCartRequest(requestUrl)) {
          setTimeout(refreshCartFrequencyLock, 300);
          setTimeout(refreshCartFrequencyLock, 900);
          setTimeout(refreshCartFrequencyLock, 1600);
        }

        return response;
      });
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._veeCartUrl = String(url || '');
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      this.addEventListener('load', function () {
        if (isCartRequest(this._veeCartUrl)) {
          setTimeout(refreshCartFrequencyLock, 300);
          setTimeout(refreshCartFrequencyLock, 900);
          setTimeout(refreshCartFrequencyLock, 1600);
        }
      });

      return originalSend.apply(this, arguments);
    };

    document.addEventListener('cart:updated', refreshCartFrequencyLock);
    document.addEventListener('cart:refresh', refreshCartFrequencyLock);
    document.addEventListener('cart:change', refreshCartFrequencyLock);
    document.addEventListener('ajaxProduct:added', refreshCartFrequencyLock);
    document.addEventListener('product:added', refreshCartFrequencyLock);
  }

  function start() {
    watchCartChanges();

    loadCartFrequencyLock().then(function () {
      runRepeatedly();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();